import React, { useEffect, useState } from 'react'

import Modal from '../reusable/modal'
import { usePet } from '../../modules'

const badgeClasses = {
    active: 'bg-green-100 text-green-700 border-green-200',
    inactive: 'bg-red-100 text-red-700 border-red-200',
    unknown: 'bg-yellow-100 text-yellow-700 border-yellow-200',
}

const EligibilityCheckModal = ({ visible, patient, scheme, onProceed, onCancel }) => {
    const [result, setResult] = useState()
    const [overrideReason, setOverrideReason] = useState('')

    const [{ loading, error }, runCheck] = usePet('eligibilityCheck', {
        patient: patient?._id,
        scheme: scheme?._id,
    }, {
        notify: true,
        manual: true,
        callback: ({ data }) => setResult(data),
        load: { msg: 'Checking eligibility...' }
    })

    useEffect(() => {
        if (!visible || !patient?._id || !scheme?._id) return

        setResult(null)
        setOverrideReason('')
        runCheck()
    }, [visible, patient?._id, scheme?._id])

    const status = result?.status ?? 'unknown'
    const requiresOverride = Boolean(error) || (result && status !== 'active')
    const canProceed = !loading && (!requiresOverride || overrideReason.trim().length >= 5)

    return (
        <Modal
            w={700}
            title="Patient Eligibility Check"
            toggle={onCancel}
            visible={visible}>
            <div className="space-y-4">
                <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4">
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Patient</p>
                        <p className="text-sm font-medium mb-0">{patient?.name}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Scheme</p>
                        <p className="text-sm font-medium mb-0">{scheme?.scheme?.name ?? scheme?.name}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Insurer</p>
                        <p className="text-sm font-medium mb-0">{scheme?.scheme?.operatedBy?.name ?? 'SHA-linked insurer'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Beneficiary CR ID</p>
                        <p className="text-sm font-medium mb-0">{patient?.beneficiaryCrId ?? 'Missing'}</p>
                    </div>
                </div>

                {
                    loading &&
                    <div className="rounded-sm border border-blue-200 bg-blue-50 px-4 py-3">
                        <p className="text-sm text-blue-700 mb-0">Checking eligibility with the insurer...</p>
                    </div>
                }

                {
                    !loading && result &&
                    <div className="rounded-sm border border-gray-200 px-4 py-4">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Coverage Status</p>
                                <span className={`inline-flex border rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClasses[status] ?? badgeClasses.unknown}`}>
                                    {status}
                                </span>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Available Balance</p>
                                <p className="text-sm font-medium mb-0">KSh {Number(result?.available_balance ?? 0).toLocaleString()}</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4 mb-4">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Beneficiary Name</p>
                                <p className="text-sm font-medium mb-0">{result?.beneficiary_name ?? patient?.name ?? '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Checked At</p>
                                <p className="text-sm font-medium mb-0">{result?.timestamp ? new Date(result.timestamp).toLocaleString() : 'Just now'}</p>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Sub-benefits</p>
                            <div className="space-y-2">
                                {
                                    (result?.sub_benefits ?? []).length > 0 ?
                                        (result?.sub_benefits ?? []).map((benefit, key) =>
                                            <div key={key} className="flex items-center justify-between rounded-sm bg-gray-50 px-3 py-2">
                                                <div>
                                                    <p className="text-sm font-medium mb-0">{benefit.name}</p>
                                                    <p className="text-xs text-gray-500 mb-0">{benefit.code}</p>
                                                </div>
                                                <p className="text-sm mb-0">{benefit.used}/{benefit.limit}</p>
                                            </div>
                                        )
                                        :
                                        <div className="rounded-sm bg-gray-50 px-3 py-2">
                                            <p className="text-sm text-gray-500 mb-0">No sub-benefits were returned for this eligibility check.</p>
                                        </div>
                                }
                            </div>
                        </div>
                    </div>
                }

                {
                    !loading && error &&
                    <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3">
                        <p className="text-sm text-red-700 mb-0">We could not complete the eligibility check right now. You can retry or proceed with a manual override.</p>
                    </div>
                }

                {
                    requiresOverride &&
                    <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-4 py-4">
                        <p className="text-sm font-medium text-yellow-800 mb-1">Manual override required</p>
                        <p className="text-xs text-yellow-800 mb-3">
                            We are about to continue without an active eligibility confirmation. Add a short note so the next step is explicit for staff.
                        </p>
                        <textarea
                            value={ overrideReason }
                            onChange={ e => setOverrideReason(e.target.value) }
                            rows={ 3 }
                            placeholder="Why are we proceeding anyway?"
                            className="appearance-none block w-full bg-white text-gray-700 border border-yellow-300 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        />
                    </div>
                }

                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="btn bg-white border border-gray-300 hover:bg-gray-50">
                        Cancel
                    </button>
                    <button onClick={runCheck} className="btn bg-white border border-gray-300 hover:bg-gray-50">
                        Retry
                    </button>
                    <button
                        onClick={() => onProceed({
                            result,
                            override: requiresOverride ? {
                                reason: overrideReason.trim(),
                                status,
                                checkedAt: result?.timestamp ?? new Date().toISOString(),
                            } : null,
                        })}
                        disabled={!canProceed}
                        className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                        {result?.status === 'active' ? 'Proceed to Consent' : 'Proceed Anyway'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default EligibilityCheckModal
